import { createTheme } from '@mui/material';
import colors from '@phyllome/helpers/colors';

export type AppThemeMode = 'light' | 'dark' | 'kirsty';

type CustomColor = {
  main: string;
  contrastText?: string;
};

const customColors = {
  sunlight: { main: colors.sunlight, contrastText: '#000000' },
  ripening: { main: colors.ripening, contrastText: '#FFFFFF' },
  budding: { main: colors.budding, contrastText: '#000000' },
  vegetative: { main: colors.vegetative, contrastText: '#000000' },
  seedling: { main: colors.seedling, contrastText: '#000000' },
  night: { main: colors.night, contrastText: '#FFFFFF' },
  earth3: { main: colors.earth3, contrastText: '#FFFFFF' },
  earth2: { main: colors.earth2, contrastText: '#FFFFFF' },
  earth1: { main: colors.earth1, contrastText: '#FFFFFF' },
  fog: { main: colors.fog, contrastText: '#000000' },
  ocean: { main: colors.ocean, contrastText: '#FFFFFF' },
  river: { main: colors.river, contrastText: '#FFFFFF' },
  clouds: { main: '#aaa', contrastText: '#000000' },
  storm: { main: '#333', contrastText: '#FFFFFF' },
} as const;

type CustomColorName = keyof typeof customColors;

const createPaletteOverrides = (colors: Record<CustomColorName, CustomColor>) => {
  return Object.entries(colors).reduce((acc, [key, value]) => {
    acc[key as CustomColorName] = value;
    return acc;
  }, {} as Record<CustomColorName, CustomColor>);
};

const createBadgeVariants = (colors: Record<CustomColorName, CustomColor>) => {
  return Object.entries(colors).map(([colorName, colorValue]) => ({
    props: { color: colorName as CustomColorName },
    style: {
      '& .MuiBadge-badge': {
        backgroundColor: colorValue.main,
        color: colorValue.contrastText,
      },
    },
  }));
};

declare module '@mui/material/styles' {
  interface Palette {
    sunlight: Palette['primary'];
    ripening: Palette['primary'];
    budding: Palette['primary'];
    vegetative: Palette['primary'];
    seedling: Palette['primary'];
    night: Palette['primary'];
    earth3: Palette['primary'];
    earth2: Palette['primary'];
    earth1: Palette['primary'];
    fog: Palette['primary'];
    ocean: Palette['primary'];
    river: Palette['primary'];
    clouds: Palette['primary'];
    storm: Palette['primary'];
  }

  interface PaletteOptions {
    sunlight: PaletteOptions['primary'];
    ripening: PaletteOptions['primary'];
    budding: PaletteOptions['primary'];
    vegetative: PaletteOptions['primary'];
    seedling: PaletteOptions['primary'];
    night: PaletteOptions['primary'];
    earth3: PaletteOptions['primary'];
    earth2: PaletteOptions['primary'];
    earth1: PaletteOptions['primary'];
    fog: PaletteOptions['primary'];
    ocean: PaletteOptions['primary'];
    river: PaletteOptions['primary'];
    clouds: PaletteOptions['primary'];
    storm: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    sunlight: true;
    ripening: true;
    budding: true;
    vegetative: true;
    seedling: true;
    night: true;
    earth3: true;
    earth2: true;
    earth1: true;
    fog: true;
    ocean: true;
    river: true;
    clouds: true;
    storm: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    sunlight: true;
    ripening: true;
    budding: true;
    vegetative: true;
    seedling: true;
    night: true;
    earth3: true;
    earth2: true;
    earth1: true;
    fog: true;
    ocean: true;
    river: true;
    clouds: true;
    storm: true;
  }
}

declare module '@mui/material/Badge' {
  interface BadgePropsColorOverrides {
    sunlight: true;
    ripening: true;
    budding: true;
    vegetative: true;
    seedling: true;
    night: true;
    earth3: true;
    earth2: true;
    earth1: true;
    fog: true;
    ocean: true;
    river: true;
    clouds: true;
    storm: true;
  }
}

declare module '@mui/material/Icon' {
  interface IconPropsColorOverrides {
    sunlight: true;
    ripening: true;
    budding: true;
    vegetative: true;
    seedling: true;
    night: true;
    earth3: true;
    earth2: true;
    earth1: true;
    fog: true;
    ocean: true;
    river: true;
    clouds: true;
    storm: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    sunlight: true;
    ripening: true;
    budding: true;
    vegetative: true;
    seedling: true;
    night: true;
    earth3: true;
    earth2: true;
    earth1: true;
    fog: true;
    ocean: true;
    river: true;
    clouds: true;
    storm: true;
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    sunlight: true;
    ripening: true;
    budding: true;
    vegetative: true;
    seedling: true;
    night: true;
    earth3: true;
    earth2: true;
    earth1: true;
    fog: true;
    ocean: true;
    river: true;
    clouds: true;
    storm: true;
  }
}

const kirstyColors: Record<CustomColorName, CustomColor> = {
  sunlight: { main: '#FFF1F5', contrastText: '#000000' },
  ripening: { main: '#E0218A', contrastText: '#FFFFFF' },
  budding: { main: '#FF69B4', contrastText: '#000000' },
  vegetative: { main: '#FF87CE', contrastText: '#000000' },
  seedling: { main: '#FFB6D9', contrastText: '#000000' },
  night: { main: '#8a1a4a', contrastText: '#FFFFFF' },
  earth3: { main: '#C71585', contrastText: '#FFFFFF' },
  earth2: { main: '#D6438D', contrastText: '#FFFFFF' },
  earth1: { main: '#E85A9A', contrastText: '#FFFFFF' },
  fog: { main: '#FFE5F0', contrastText: '#000000' },
  ocean: { main: '#FF1493', contrastText: '#FFFFFF' },
  river: { main: '#FF77B8', contrastText: '#FFFFFF' },
  clouds: { main: '#FFD1DC', contrastText: '#000000' },
  storm: { main: '#B0006A', contrastText: '#FFFFFF' },
};

const createAppTheme = (mode: AppThemeMode) => {
  if (mode === 'kirsty') {
    return createTheme({
      typography: {
        fontFamily: ['"Comic Sans MS"', '"Comic Sans"', 'cursive'].join(','),
        fontWeightBold: 900,
      },
      shape: {
        borderRadius: 16,
      },
      palette: {
        mode: 'light',
        primary: {
          main: '#E0218A',
          contrastText: '#FFFFFF',
        },
        secondary: {
          main: '#FF1493',
          contrastText: '#FFFFFF',
        },
        warning: {
          main: '#C71585',
        },
        error: {
          main: '#8B0000',
        },
        background: {
          default: '#FFE5F0',
          paper: '#FFF1F5',
        },
        text: {
          primary: '#8a1a4a',
          secondary: '#C71585',
        },
        divider: '#FFB6D9',
        ...createPaletteOverrides(kirstyColors),
      },
      components: {
        MuiBadge: {
          variants: createBadgeVariants(kirstyColors),
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 999,
              fontWeight: 700,
              textTransform: 'none',
            },
          },
          variants: Object.entries(kirstyColors).map(([colorName, colorValue]) => ({
            props: { color: colorName as CustomColorName },
            style: {
              '&:hover': {
                backgroundColor: colorValue.main + '99',
              },
            },
          })),
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
          },
        },
      },
    });
  }

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.ripening,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: colors.earth3,
      },
      warning: {
        main: '#b91111',
      },
      ...(mode === 'dark'
        ? {
          background: {
            default: '#121212',
            paper: '#1e1e1e',
          },
        }
        : {}),
      ...createPaletteOverrides(customColors),
    },
    components: {
      MuiBadge: {
        variants: createBadgeVariants(customColors),
      },
      MuiButton: {
        variants: Object.entries(customColors).map(([colorName, colorValue]) => ({
          props: { color: colorName as CustomColorName },
          style: {
            '&:hover': {
              backgroundColor: colorValue.main + '99',
            },
          },
        })),
      },
    },
  });
};

const muiTheme = createAppTheme('light');

export default muiTheme;
export { createAppTheme };
export type { CustomColorName };
